﻿import { useEffect, useState } from "react";
import { AdminCard, AdminCardHeader, AdminPagination, AdminSearchInput, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import type { MemberItem } from "@/types.ts";
interface AccountTableProps {
    members: MemberItem[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
    searchQuery: string;
    onSearchChange: (value: string) => void;
    activeFilter: string;
    loading: boolean;
}
const accountIdOf = (member: MemberItem) => member.userAccountId;
const accountKeyOf = (member: MemberItem) => {
    const accountId = accountIdOf(member);
    return accountId == null ? null : String(accountId);
};
const userTypeLabels: Record<string, string> = {
    SENIOR: "Senior",
    GUARDIAN: "Keeper",
};
const accountTypeLabels: Record<string, string> = {
    USER_ACCOUNT: "사용자 계정",
};
const statusLabels: Record<string, string> = {
    ACTIVE: "활성",
    INACTIVE: "비활성",
    DELETED: "삭제",
};
const roleStyleMap: Record<string, {
    bg: string;
    text: string;
    border: string;
}> = {
    SENIOR: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
    GUARDIAN: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
};
const AccountTable = ({ members, selectedKey, onSelect, searchQuery, onSearchChange, activeFilter, loading, }: AccountTableProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(members.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const pageItems = members.slice(startIdx, startIdx + pageSize);
    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter, searchQuery]);
    return (<AdminCard className="overflow-hidden">
      <AdminCardHeader icon="ri-list-check" title="계정 목록" action={<div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">총 {members.length}건</span>
            {activeFilter !== "all" && <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">필터 적용 중</span>}
            <AdminSearchInput value={searchQuery} onChange={onSearchChange} placeholder="이름, 로그인 ID, 연락처 검색" className="w-56" />
          </div>}/>

      <AdminTable headers={["계정", "사용자 유형", "이름", "연락처", "상태", "생성일"]} loading={loading} emptyMessage={!loading && pageItems.length === 0 ? "표시할 계정이 없습니다." : undefined}>
        {pageItems.map((member) => {
            const accountKey = accountKeyOf(member);
            const isSelected = accountKey === selectedKey;
            const typeStyleKey = member.userType ?? member.accountType;
            const roleStyle = roleStyleMap[typeStyleKey] ?? roleStyleMap.GUARDIAN;
            const typeLabel = member.userType ? userTypeLabels[member.userType] ?? member.userType : "-";
            return (<AdminTr key={accountKey ?? `${member.accountType}:${member.loginId}`} className={`cursor-pointer ${isSelected ? "bg-blue-50/60" : "hover:bg-slate-50/60"}`} onClick={() => accountKey && onSelect(accountKey)}>
              <AdminTd className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-slate-400 text-xs"/>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-slate-700 block">{member.loginId}</span>
                    <span className="text-xs text-slate-400">계정 ID {member.userAccountId ?? "-"}</span>
                  </div>
                </div>
              </AdminTd>
              <AdminTd>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${roleStyle.bg} ${roleStyle.text} ${roleStyle.border}`}>
                  {typeLabel}
                </span>
                <span className="block mt-1 text-xs text-slate-400">{accountTypeLabels[member.accountType] ?? member.accountType}</span>
              </AdminTd>
              <AdminTd>
                <span className="text-xs text-slate-600">{member.name}</span>
              </AdminTd>
              <AdminTd>
                <span className="text-xs text-slate-600">{member.phone ?? "-"}</span>
              </AdminTd>
              <AdminTd>
                <AdminStatusBadge group="ACCOUNT_STATUS" code={member.status} label={statusLabels[member.status] ?? member.status} />
              </AdminTd>
              <AdminTd>
                <span className="text-xs text-slate-500">{formatDateTime(member.createdAt)}</span>
              </AdminTd>
            </AdminTr>);
        })}
      </AdminTable>

      <AdminPagination currentPage={safePage} totalPages={totalPages} totalCount={members.length} pageSize={pageSize} itemLabel="건" onPageChange={setCurrentPage}/>
    </AdminCard>);
};
export default AccountTable;
